import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { desktopScreenRecordingMessages } from "@/i18n/messages/desktop-screen-recording";
import { cn } from "@/lib/cn";
import {
  removeScreenRecordingClip,
  type ScreenRecordingClip,
} from "./screen-recording-store";
import { ScreenRecordingPreview } from "./screen-recording-preview";
import { screenRecordingClipLabel } from "./screen-recording-clip-label";
import {
  ScreenRecordingUploadStatus,
  ScreenRecordingVideo,
} from "./screen-recording-video";
import { useScreenRecordingElapsed } from "./use-screen-recording-elapsed";

export function ScreenRecordingClipCard({
  clip,
  number,
  status = false,
  showVideo = true,
}: {
  clip: ScreenRecordingClip;
  number: number;
  status?: boolean;
  showVideo?: boolean;
}) {
  const elapsed = useScreenRecordingElapsed(clip);
  const [previewOpen, setPreviewOpen] = useState(false);
  const playRef = useRef<HTMLButtonElement>(null);
  const closePreview = useCallback(() => setPreviewOpen(false), []);
  const t = useT(desktopScreenRecordingMessages);
  const label = screenRecordingClipLabel({
    number,
    phase: clip.phase,
    elapsedSeconds: elapsed,
    durationMs: clip.durationMs ?? 0,
    error: clip.error,
    status,
  });

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col rounded-preview px-2 text-appkit-caption text-secondary",
        status ? "py-1" : "bg-glass-fill-heavy",
      )}
      data-testid={`screen-recording-clip-${clip.clipId}`}
    >
      <div className="flex items-center gap-1">
        <span
          className="max-w-64 truncate"
          title={clip.path || clip.error || label}
        >
          {label}
        </span>
        {clip.phase === "ready" && clip.path ? (
          <Button
            ref={playRef}
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label={t("playClip", { number })}
            data-testid={`screen-recording-play-${clip.clipId}`}
            aria-expanded={previewOpen}
            aria-haspopup="dialog"
            onClick={() => setPreviewOpen((open) => !open)}
          >
            <Icons.Play className="h-3 w-3" aria-hidden="true" />
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          aria-label={t("removeClip", { number })}
          data-testid={`screen-recording-remove-${clip.clipId}`}
          onClick={() => removeScreenRecordingClip(clip.clipId)}
        >
          <Icons.X className="h-3 w-3" aria-hidden="true" />
        </Button>
      </div>
      {status && (clip.phase === "starting" || clip.phase === "recording") ? (
        <span>{t("releaseToFinish")}</span>
      ) : null}
      {clip.phase === "ready" ? (
        <>
          {showVideo ? (
            <div className="max-w-64 py-1">
              <ScreenRecordingVideo clipId={clip.clipId} />
            </div>
          ) : null}
          <ScreenRecordingUploadStatus clipId={clip.clipId} />
        </>
      ) : null}
      {previewOpen && clip.path ? (
        <ScreenRecordingPreview
          clipId={clip.clipId}
          number={number}
          path={clip.path}
          poster={clip.frames?.[0]?.dataUrl}
          anchorRef={playRef}
          onClose={closePreview}
        />
      ) : null}
    </div>
  );
}
